import { normalizeName } from '../shared/naming';
import type { JobRoleId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import type { MeasureBy } from './geometry';
import {
  guessAppliesTo,
  guessMeasureOfOpening,
  guessMeasureOfPiece,
  mentionsRailing,
  type AppliesTo,
} from './applies-to';
import type { IndexAverage, IndexOrigin } from './index-origin';
import type { ItemKind } from './takeoff-line';
import {
  isSteelPurchaseService,
  parseSteelGauge,
  type ElementSteelRates,
  type SteelGauge,
  type SteelRate,
} from './steel';
import type { UnitTypeKind } from './unit-type';
import {
  isTotalSheet,
  summarizeSheet,
  type ImportedItem,
  type ServiceSheet,
  type SheetSummary,
} from './service-sheet';

/**
 * A pasta inteira virada em **catálogo** — disciplina → serviço → item.
 *
 * `service-sheet.ts` lê uma aba. Aqui o assunto é outro: a planilha da obra tem uma aba
 * por tipologia, e as mesmas quarenta linhas de serviço se repetem em todas elas. O
 * catálogo é o que sobra quando essa repetição é removida — cada serviço uma vez, com a
 * quantidade que **cada aba** declarou guardada ao lado.
 *
 * Guardar a quantidade por aba, e não somada, é o que torna a comparação com o traçado
 * possível: a aba "Apartamento Tipo 1" é comparável com a tipologia que alguém traçou
 * sobre a planta; a soma das oito abas não é comparável com nada.
 */

// ── O que cada aba é ────────────────────────────────────────────────────────────

/**
 * O papel da aba dentro da pasta.
 *
 * A distinção existe porque importar tudo contaria o prédio duas vezes: a aba "Andar
 * Tipo" já é a soma das outras. E porque nem toda aba que não é a soma é uma parcela dela
 * — a `ROOFTOP` da planilha do cliente é a **cobertura**, um andar à parte, com 21
 * disciplinas contra 12 das tipologias. Somá-la ao andar tipo não daria o prédio, daria
 * outra coisa.
 */
export type SheetScope =
  /**
   * A aba que soma as outras — o "andar tipo" da planilha.
   *
   * Fica **fora** do quantitativo: importá-la junto contaria o prédio duas vezes, e ela é
   * derivada de qualquer forma. Serve de conferência da leitura, não de fonte.
   */
  | 'SOMA'
  /** Uma parcela dessa soma — uma tipologia do pavimento. */
  | 'PARCELA'
  /**
   * Escopo próprio: a maioria dos serviços está fora da soma, logo não é parcela dela.
   *
   * Na planilha da obra é a `ROOFTOP` — **a cobertura, um andar à parte**, confirmado com
   * o engenheiro em 05/09/2026. Daí os 91 serviços contra os 40 de uma tipologia: ela não
   * é um apartamento a mais no pavimento tipo, é outro pavimento.
   *
   * A consequência prática é o multiplicador: parcela repete com o pavimento tipo, escopo
   * próprio acontece **uma vez** (ver `CREATE_TYPOLOGIES_FROM_SHEETS`).
   */
  | 'PROPRIO'
  /** Capa, resumo, rascunho — nada de hierarquia reconhecível. */
  | 'VAZIA';

export const SHEET_SCOPE_LABEL: Readonly<Record<SheetScope, string>> = {
  SOMA: 'soma das outras',
  PARCELA: 'parcela da soma',
  PROPRIO: 'escopo próprio',
  VAZIA: 'nada reconhecido',
};

export interface ClassifiedSheet {
  readonly sheet: ServiceSheet;
  readonly scope: SheetScope;
  readonly summary: SheetSummary;
  /**
   * Os serviços que esta aba tem e a soma não.
   *
   * Existe nas **duas** classificações, e quer dizer coisas diferentes em cada uma. Numa
   * aba de escopo próprio, é o motivo do veredito. Numa parcela, é o contrário: são os
   * serviços que a **soma esqueceu** — e essa é a informação mais útil que esta leitura
   * produz, porque aponta um buraco na planilha de quem a escreveu.
   *
   * A tela mostra os primeiros nos dois casos: um veredito que ninguém pode conferir não
   * serve para nada, e quem abriu a planilha reconhece na hora qual dos dois é.
   */
  readonly foreignServices: readonly string[];
}

/**
 * Quanto de uma aba precisa estar fora da soma para ela deixar de ser parcela.
 *
 * Não é "qualquer serviço a mais", e a planilha real ensinou isso: o `Apartamento Tipo 1`
 * traz quatro serviços de elétrica que o `Andar Tipo` não lista, e com a regra do zero ele
 * era expulso da soma inteiro — 40 serviços e 218 itens de um apartamento sumiam da
 * conferência, que passava a acusar diferença em tudo. **A conferência mentia, e o
 * culpado era o classificador.**
 *
 * Com a maioria, o mesmo arquivo se resolve: 4 de 40 é uma parcela com quatro
 * esquecimentos da soma; 52 de 91 é a `ROOFTOP`, que é outra obra.
 */
const MAIORIA = 0.5;

/** `(disciplina, serviço)` normalizado — a chave natural do serviço (RN-164). */
/**
 * O nome do item, como o item se chama.
 *
 * **Sempre em maiúsculas.** Não é estilo: é a forma em que o catálogo de compras do
 * cliente escreve os produtos, e é a forma em que a planilha do orçamento chega. Um item
 * digitado à mão em caixa baixa fica visivelmente de fora numa lista de duzentos, e
 * quem confere o pedido tropeça nele.
 *
 * A caixa **não** entra na identidade — `resolveItemKey` normaliza para minúsculas antes
 * de comparar, e "Tijolo" e "TIJOLO" já eram o mesmo item. O que muda aqui é como ele se
 * apresenta, não quem ele é.
 */
export function itemDescription(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLocaleUpperCase('pt-BR');
}

export function serviceKey(disciplineName: string, serviceName: string): string {
  return `${normalizeName(disciplineName)}|${normalizeName(serviceName)}`;
}

function serviceKeysOf(sheet: ServiceSheet): ReadonlySet<string> {
  return new Set(
    sheet.disciplines.flatMap((disciplina) =>
      disciplina.services.map((servico) => serviceKey(disciplina.name, servico.name)),
    ),
  );
}

function describeServices(
  sheet: ServiceSheet,
  keep: (chave: string) => boolean,
): readonly string[] {
  return sheet.disciplines.flatMap((disciplina) =>
    disciplina.services
      .filter((servico) => keep(serviceKey(disciplina.name, servico.name)))
      .map((servico) => `${disciplina.name} > ${servico.name}`),
  );
}

/**
 * Cada aba com o papel que ela tem.
 *
 * O papel é **derivado**, e não uma lista de nomes conhecidos: "escopo próprio" é a aba
 * cuja **maioria** dos serviços está fora da soma, e isso é conferível no arquivo. Chumbar
 * `ROOFTOP` aqui funcionaria nesta planilha e falharia calada na próxima obra, que chamará
 * a mesma coisa de outro nome.
 *
 * A maioria importa: a versão que expulsava a aba por **um** serviço a mais tirava um
 * apartamento inteiro da conferência da planilha real (ver `MAIORIA`).
 *
 * Sem aba de soma, não há de que ser parcela: todas viram escopo próprio, e a conferência
 * simplesmente não acontece.
 */
export function classifySheets(sheets: readonly ServiceSheet[]): readonly ClassifiedSheet[] {
  const soma = sheets.find((sheet) => isTotalSheet(sheet.name) && sheet.disciplines.length > 0);
  const daSoma = soma === undefined ? undefined : serviceKeysOf(soma);

  return sheets.map((sheet): ClassifiedSheet => {
    const summary = summarizeSheet(sheet);

    if (summary.services === 0) return { sheet, scope: 'VAZIA', summary, foreignServices: [] };
    if (sheet === soma) return { sheet, scope: 'SOMA', summary, foreignServices: [] };

    const forasteiros = describeServices(
      sheet,
      (chave) => daSoma === undefined || !daSoma.has(chave),
    );

    return {
      sheet,
      // A **maioria** fora da soma, e não um serviço a mais: ver `MAIORIA`.
      scope: forasteiros.length > summary.services * MAIORIA ? 'PROPRIO' : 'PARCELA',
      summary,
      foreignServices: forasteiros,
    };
  });
}

// ── A conferência ───────────────────────────────────────────────────────────────

export interface ConferenceLine {
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly unit?: string;
  /** Somando as parcelas. */
  readonly parts: number;
  /** O que a aba de soma declara. */
  readonly total: number;
  readonly difference: number;
}

export interface SheetConference {
  /** Ausente = não havia aba de soma, e nada foi conferido. */
  readonly totalSheetName?: string;
  readonly checked: number;
  readonly matching: number;
  /** Só o que não bateu — a lista inteira seriam quarenta linhas dizendo "certo". */
  readonly divergences: readonly ConferenceLine[];
}

/** Duas casas: a planilha da obra mede em m² e m com duas, e o centavo importa. */
const CONFERENCE_DECIMALS = 2;

/**
 * A soma das parcelas bate com a aba de soma?
 *
 * **A aba de soma não é juiz.** Ela é uma conta que alguém fez dentro da planilha, e o
 * andar tipo é, por definição, os apartamentos mais a escada mais a área comum — quem tem
 * a informação são as parcelas. O quantitativo do sistema sai delas, sempre; esta função
 * não escolhe entre os dois lados, ela só mostra onde discordam.
 *
 * O que a comparação continua valendo é como **segundo olhar sobre a leitura**, e é aí
 * que ela paga por si: divergência espalhada por todos os serviços denuncia coluna
 * deslocada ou aba de fora, que é defeito nosso e silencioso. Diferença em alguns é a
 * planilha desatualizada, que é defeito de lá — e foi para isso que a obra pediu um
 * sistema.
 *
 * Serviço que só existe de um dos lados **também aparece**, com o outro lado em zero.
 * Escondê-lo tiraria de vista justamente o caso mais informativo: o serviço que a soma da
 * planilha esqueceu.
 */
export function conferSheets(classified: readonly ClassifiedSheet[]): SheetConference {
  const soma = classified.find((aba) => aba.scope === 'SOMA');
  if (soma === undefined) return { checked: 0, matching: 0, divergences: [] };

  interface Acumulado {
    readonly disciplineName: string;
    readonly serviceName: string;
    readonly unit?: string;
    parts: number;
    total: number;
  }
  const porServico = new Map<string, Acumulado>();

  const acumular = (aba: ClassifiedSheet, campo: 'parts' | 'total'): void => {
    for (const disciplina of aba.sheet.disciplines) {
      for (const servico of disciplina.services) {
        const chave = serviceKey(disciplina.name, servico.name);
        const atual =
          porServico.get(chave) ??
          ({
            disciplineName: disciplina.name,
            serviceName: servico.name,
            ...(servico.unit === undefined ? {} : { unit: servico.unit }),
            parts: 0,
            total: 0,
          } satisfies Acumulado);
        atual[campo] += servico.quantity ?? 0;
        porServico.set(chave, atual);
      }
    }
  };

  for (const parcela of classified.filter((aba) => aba.scope === 'PARCELA')) {
    acumular(parcela, 'parts');
  }
  acumular(soma, 'total');

  const linhas = [...porServico.values()].map((acumulado): ConferenceLine => {
    const parts = roundQuantity(acumulado.parts, CONFERENCE_DECIMALS);
    const total = roundQuantity(acumulado.total, CONFERENCE_DECIMALS);
    return {
      disciplineName: acumulado.disciplineName,
      serviceName: acumulado.serviceName,
      ...(acumulado.unit === undefined ? {} : { unit: acumulado.unit }),
      parts,
      total,
      difference: roundQuantity(parts - total, CONFERENCE_DECIMALS),
    };
  });

  return {
    totalSheetName: soma.sheet.name,
    checked: linhas.length,
    matching: linhas.filter((linha) => linha.difference === 0).length,
    divergences: linhas.filter((linha) => linha.difference !== 0),
  };
}

// ── Como o serviço mede ─────────────────────────────────────────────────────────

/** `"ELEVAÇÃO"` → `"elevacao"`. A planilha escreve em caixa alta e sem constância. */
function semAcento(value: string): string {
  return normalizeName(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * A palavra do serviço que denuncia como ele mede.
 *
 * A unidade sozinha não resolve: alvenaria, reboco, piso e forro são **todos** m², e
 * confundir os quatro é errar o quantitativo inteiro sem que nenhum número pareça
 * estranho. O nome resolve, porque obra nomeia serviço pelo que ele faz.
 *
 * A ordem é a da especificidade — "contrapiso" antes de "piso" —, e o primeiro termo
 * encontrado decide.
 */
const TERMOS_POR_MEDIDA: readonly {
  readonly measure: MeasureBy;
  readonly terms: readonly string[];
}[] = [
  // Rodapé e meia-cana correm **entre** as portas; o cordão corre pelo perímetro cheio. Por
  // isso os dois grupos existem: o primeiro desconta a porta, o segundo não.
  //
  // Soleira e peitoril saíram daqui (RN-309): eles **são** o vão, e medem a largura dele com
  // a margem de cada lado — quem os reconhece é `guessMeasureOfPiece`, antes desta lista.
  { measure: 'SKIRTING', terms: ['rodape', 'meia cana'] },
  { measure: 'PERIMETER', terms: ['cordao'] },
  // Espala vem **antes** do revestimento de propósito: "REBOCO DE ESPALA" tem as duas
  // palavras, e é a espala que decide como ele mede. Com FACE ganhando, o serviço puxaria
  // a área das faces da tipologia inteira em vez do metro de quina.
  { measure: 'ESPALA', terms: ['espala', 'espaleta', 'quina', 'requadro'] },
  { measure: 'CEILING', terms: ['forro', 'teto', 'rebaixo'] },
  { measure: 'FLOOR', terms: ['contrapiso', 'piso', 'regularizacao', 'nivelamento'] },
  {
    measure: 'WALL',
    terms: ['alvenaria', 'elevacao', 'bloco', 'tijolo', 'divisoria', 'drywall', 'verga'],
  },
  {
    measure: 'FACE',
    terms: [
      'chapisco',
      'emboco',
      'reboco',
      'massa',
      'pintura',
      'textura',
      'gesso',
      'azulejo',
      'ceramica',
      'porcelanato',
      'revestimento',
      'selador',
      'fachada',
    ],
  },
];

/**
 * Um palpite de como o serviço mede, para a tela nascer preenchida.
 *
 * É **palpite**, e a tela diz isso: quem confere a importação corrige o que estiver
 * errado, e é a correção que vale. Nascer vazio custaria quarenta escolhas manuais para
 * chegar onde o nome do serviço já dizia; nascer preenchido sem avisar seria pior, porque
 * ninguém confere o que parece pronto.
 *
 * Sem palavra reconhecida, cai em `UNIT` — o único valor que **não** deriva número da
 * geometria. Errar para o lado de não calcular nada é errar para o lado seguro.
 */
export function guessMeasureBy(name: string, unit: string | undefined): MeasureBy {
  // O guarda-corpo vem antes de tudo: "PINTURA DE GUARDA-CORPO" tem a palavra da face, e a
  // face mediria as paredes da tipologia inteira. Palpite de metro, que é como se compra
  // (D25); quem compra o de vidro troca para a área no cadastro.
  if (mentionsRailing(name)) return 'LENGTH';
  // Fôrma, escoramento e batente dizem a peça **e** a conta, e vêm antes da lista: "FÔRMA DE
  // PILAR" não tem palavra que a lista conheça, e "REQUADRO DE JANELA" tem uma que a lista
  // entenderia errado (Fase 24, fatia 10).
  const daPeca = guessMeasureOfPiece(name);
  if (daPeca !== undefined) return daPeca;

  const texto = semAcento(name);
  for (const { measure, terms } of TERMOS_POR_MEDIDA) {
    if (terms.some((termo) => texto.includes(termo))) return measure;
  }

  // O serviço que só nomeia um vão — "PORTAS" — conta o vão. Depois da lista, para que
  // "PINTURA DE PORTA" continue sendo o que era.
  const doVao = guessMeasureOfOpening(name);
  if (doVao !== undefined) return doVao;

  // O metro linear e o metro cúbico se decidem pela unidade. `M²` serve a quatro medidas
  // diferentes e continua dependendo do nome, que já falhou acima; `M³` serve a uma só —
  // concreto — e por isso a unidade basta (RN-188).
  // `M³` e `M3` são a mesma unidade escrita de dois jeitos, e as duas chegam da planilha:
  // o expoente é caractere de compatibilidade e não cai na normalização de acento.
  const unidade = semAcento(unit ?? '').replace(/\s/g, '');
  if (unidade === 'm3' || unidade === 'm³') return 'VOLUME';
  return unidade === 'm' ? 'PERIMETER' : 'UNIT';
}

/**
 * Apartamento ou área comum, pelo nome da aba.
 *
 * A distinção não é cosmética: a área comum muda o rateio da divisa (RN-163) e é ela que
 * recebe a face da parede que dá para o hall (RN-178). Nascer tudo como apartamento faria
 * a escada e a circulação ratearem parede com o vizinho, que é errado.
 *
 * É palpite, como o de `guessMeasureBy`, e pelo mesmo motivo: acertar sete de oito e deixar
 * a oitava para alguém corrigir custa menos do que oito escolhas manuais. A tela mostra o
 * tipo de cada tipologia e permite trocá-lo.
 */
const TERMOS_DE_AREA_COMUM = [
  'circulacao',
  'escada',
  'hall',
  'corredor',
  'area comum',
  'elevador',
  'lazer',
];

/**
 * As unidades que dizem "isto é hora de gente".
 *
 * `H/H` é como a planilha da obra escreve homem-hora, e é o sinal mais confiável que
 * existe: é a **unidade da regra** (RN-166), não um palpite sobre o nome. Nos doze itens
 * de mão de obra do arquivo do cliente, todos os doze vêm assim.
 */
const UNIDADES_DE_HORA = ['h/h', 'hh', 'h', 'hora', 'horas'];

/** Nome de função, para o item que veio sem unidade. */
const FUNCOES = [
  'pedreiro',
  'servente',
  'ajudante',
  'oficial',
  'encarregado',
  'mestre de obra',
  'carpinteiro',
  'armador',
  'eletricista',
  'encanador',
  'bombeiro hidraulico',
  'pintor',
  'azulejista',
  'gesseiro',
  'soldador',
  'operador',
  'vigia',
  'apontador',
];

/** A disciplina ou o serviço já se declara: "MAO DE OBRA ALVENARIA". */
function falaDeMaoDeObra(texto: string): boolean {
  return texto.includes('mao de obra') || texto.includes('m.o.');
}

/**
 * O que o item é, pelo que a planilha diz dele.
 *
 * A ordem é a da confiança. **A unidade decide primeiro**: `H/H` é a unidade da RN-166 e
 * não serve para mais nada — nenhum material se compra em homem-hora. Depois vem o lugar
 * onde ele está, porque a planilha da obra separa o que compra do que contrata em
 * disciplina e serviço próprios ("FERRAMENTAS", "EQUIPAMENTOS", "INSUMOS / CONSUMIVEIS",
 * "MAO DE OBRA TERCERIZADA"). Por último o nome do próprio item, que pega o "PEDREIRO"
 * solto numa linha que ninguém marcou.
 *
 * Sem nada reconhecido, `MATERIAL` — que é o que a esmagadora maioria é, e o palpite fica
 * visível na tela para quem confere trocar.
 */
export function guessItemKind(input: {
  readonly disciplineName: string;
  readonly serviceName: string;
  readonly description: string;
  readonly unit?: string;
}): ItemKind {
  const unidade = semAcento(input.unit ?? '').replace(/\s/g, '');
  if (UNIDADES_DE_HORA.includes(unidade)) return 'MAO_DE_OBRA';

  const ondeEstá = semAcento(`${input.disciplineName} ${input.serviceName}`);
  const oNome = semAcento(input.description);
  if (falaDeMaoDeObra(ondeEstá) || falaDeMaoDeObra(oNome)) return 'MAO_DE_OBRA';

  // O **lugar** vem antes do nome do item, e a ordem não é detalhe: "COLHER DE PEDREIRO"
  // dentro de FERRAMENTAS é ferramenta, não gente. Com o nome decidindo primeiro, a caixa
  // de ferramentas de cada disciplina virava folha de pagamento.
  if (ondeEstá.includes('ferramenta')) return 'FERRAMENTA';
  if (ondeEstá.includes('equipamento')) return 'EQUIPAMENTO';
  if (ondeEstá.includes('insumo') || ondeEstá.includes('consumivel')) return 'INSUMO';

  if (FUNCOES.some((funcao) => oNome.includes(funcao))) return 'MAO_DE_OBRA';

  return 'MATERIAL';
}

export function guessUnitTypeKind(sheetName: string): UnitTypeKind {
  const texto = semAcento(sheetName);
  return TERMOS_DE_AREA_COMUM.some((termo) => texto.includes(termo)) ? 'AREA_COMUM' : 'APARTAMENTO';
}

// ── O catálogo ──────────────────────────────────────────────────────────────────

export interface CatalogItem {
  /** A descrição normalizada, ou a chave que o código já apontava (ver `resolveItemKey`). */
  readonly key: string;
  readonly code?: string;
  readonly description: string;
  readonly unit?: string;
  readonly payment?: string;
  /**
   * A quantidade que **cada aba** declarou para este item, pelo nome da aba.
   *
   * Mesmo motivo do serviço: guardada por aba, ela é comparável com a tipologia que a
   * usa; somada, não é comparável com nada. É daqui que a tela de lançamento nasce
   * preenchida — 218 campos em branco seriam 218 chances de esquecer um, e o número que a
   * construtora já orçou é o melhor ponto de partida que existe.
   */
  readonly quantityBySheet: Readonly<Record<string, number>>;
  /**
   * O que este item é — material, insumo, ferramenta, equipamento ou **mão de obra**.
   *
   * A classe é do **catálogo**, e não do lançamento: o mesmo cimento não pode ser material
   * numa linha e insumo na outra. E a quinta classe não é produto de compras, é gente —
   * é ela que mantém hora de pedreiro fora do pedido de material (RN-166).
   */
  readonly kind: ItemKind;
  /** Palpite da importação ou escolha de gente — a mesma regra da medida do serviço. */
  readonly kindOrigin: GuessOrigin;
  /**
   * A função que executa — só no item de mão de obra (RN-252).
   *
   * A mão de obra da planilha chega como texto ("PEDREIRO", "M.O. ALVENARIA") e fica sem
   * função até alguém escolher uma; a acrescentada na tela já nasce com ela, escolhida da
   * lista das funções que executam a disciplina do serviço.
   */
  readonly jobRoleId?: JobRoleId;
  /**
   * A bitola, quando o item é aço (Fase 27, RN-366 e RN-378).
   *
   * Só existe no CORTE E DOBRA DE FERRAGEM, que é o único serviço que compra aço. O item com
   * bitola não usa o índice: a quantidade dele é o medido do serviço, em kg, vezes a
   * participação da bitola na taxa da disciplina (`gaugeShares`).
   */
  readonly steelGauge?: SteelGauge;
  /**
   * O índice de consumo: quanto deste item por unidade do **medido** do serviço — 0,4 saco
   * por m², 3 HH por m³ (Fase 24, D6 e D7).
   *
   * É do cadastro, e não da tipologia: vale para todas, e o que muda entre elas é o medido.
   * A planilha não o traz, e por isso reimportá-la nunca o apaga. Ausente, ou zero, é "sem
   * índice" — a quantidade segue o digitado ou a planilha (ver `calculatedItemQuantity`).
   */
  readonly consumptionIndex?: number;
  /** A perda, em por cento, ao lado do índice (D8). Mão de obra não tem. */
  readonly lossPercent?: number;
  /**
   * De onde veio o índice (Fase 40, RN-520): a média do apartamento ou alguém. Ausente é o
   * índice de antes — digitado.
   */
  readonly indexOrigin?: IndexOrigin;
  /** A média quando o índice foi gravado — ao lado do digitado, e o "de" da faixa (D8). */
  readonly indexAverage?: IndexAverage;
}

/**
 * De onde veio a medida do serviço.
 *
 * A distinção existe por causa da reimportação. `PALPITE` é o que o nome do serviço
 * sugeriu, e ele **melhora**: no dia em que o classificador aprende que rodapé não é
 * soleira, quarenta linhas passam a medir certo — mas só se a importação seguinte puder
 * refazê-las. `DECIDIDO` é o que alguém escolheu na tela, e código nenhum reescreve isso.
 *
 * Sem o campo, as duas eram indistinguíveis e o sistema tinha de escolher entre congelar
 * o palpite para sempre ou apagar a escolha de quem conferiu. Guardando qual é qual, não
 * precisa escolher.
 */
export type GuessOrigin = 'PALPITE' | 'DECIDIDO';

/** Mantido pelo nome antigo porque é assim que a medida do serviço já se chama. */
export type MeasureOrigin = GuessOrigin;

export interface CatalogService {
  readonly key: string;
  readonly name: string;
  readonly unit?: string;
  /** Como ele mede — palpite na importação, decisão de gente depois. */
  readonly measureBy: MeasureBy;
  /**
   * A peça de onde a medida sai (Fase 24, RN-256). Ausente em volume e espala é pendente.
   *
   * Entra no mesmo `measureOrigin` da medida, e não num próprio: elemento e grandeza são um
   * par, escolhido junto na tela — palpite os dois, ou decididos os dois (RN-258).
   */
  readonly appliesTo?: AppliesTo;
  /** Qual dos dois é o `measureBy` de hoje — e o que a reimportação pode refazer. */
  readonly measureOrigin: MeasureOrigin;
  /** Espessura da camada, em metros. Não vem da planilha: é digitada para virar m³. */
  readonly layerThickness?: number;
  /**
   * Margem para cada lado do vão, em metros — a soleira e o peitoril passam do vão (RN-308).
   * Como a espessura, não vem da planilha; só conta na largura de porta, janela e passagem.
   */
  readonly sideMargin?: number;
  readonly items: readonly CatalogItem[];
  /**
   * A quantidade que **cada aba** declarou, pelo nome da aba.
   *
   * É a referência contra a qual o traçado vai ser comparado. Somada, perderia justamente
   * a comparação que interessa: a aba de uma tipologia contra a tipologia traçada.
   */
  readonly quantityBySheet: Readonly<Record<string, number>>;
}

export interface CatalogDiscipline {
  readonly key: string;
  readonly name: string;
  readonly services: readonly CatalogService[];
  /**
   * Os kg de aço por m³ de cada bitola, nesta obra (Fase 27, RN-367).
   *
   * Moram na disciplina, e não no item, para que o CORTE E DOBRA, a MONTAGEM e a INSTALAÇÃO
   * leiam o mesmo kg (ADR-176). Ausente ou vazio = a disciplina ainda não tem taxa.
   */
  readonly steelRates?: readonly SteelRate[];
  /**
   * As listas por elemento, na disciplina que mistura elementos (Fase 42, RN-573): na PILARES DE
   * CONTENÇÃO, a do muro e a do pilar. O serviço lê a do elemento dele (`steelRatesFor`); sem
   * ela, a da disciplina inteira. Ausente ou vazia: só a da disciplina.
   */
  readonly elementSteelRates?: readonly ElementSteelRates[];
}

export interface CatalogSource {
  readonly name: string;
  readonly scope: SheetScope;
  readonly summary: SheetSummary;
  /** Entrou no catálogo? A aba de soma fica de fora sem deixar de ser registrada. */
  readonly imported: boolean;
}

export interface ServiceCatalog {
  /**
   * De onde ele veio — ausente no catálogo montado à mão.
   *
   * O catálogo deixou de ser "o que a planilha trouxe" e passou a ser **o cadastro de
   * serviços da obra**. A planilha é um jeito rápido de preenchê-lo, e não a condição para
   * ele existir: quem não tem arquivo cadastra disciplina, serviço e item na tela e
   * levanta o quantitativo do mesmo jeito.
   */
  readonly fileName?: string;
  readonly importedAt?: string;
  readonly sources: readonly CatalogSource[];
  readonly disciplines: readonly CatalogDiscipline[];
}

/** O cadastro vazio — o ponto de partida de quem não tem planilha. */
export function emptyCatalog(): ServiceCatalog {
  return { sources: [], disciplines: [] };
}

/**
 * Acrescenta uma disciplina ao cadastro, se ela ainda não existe.
 *
 * A chave é o nome normalizado, como na importação: cadastrar "Alvenaria" onde já há
 * "ALVENARIA" criaria duas disciplinas que a obra vive como uma, e os serviços de cada
 * uma ficariam em listas separadas.
 */
export function addDiscipline(catalog: ServiceCatalog, name: string): ServiceCatalog {
  const nome = name.trim();
  if (nome === '') return catalog;

  const chave = normalizeName(nome);
  if (catalog.disciplines.some((disciplina) => disciplina.key === chave)) return catalog;

  return {
    ...catalog,
    disciplines: [...catalog.disciplines, { key: chave, name: nome, services: [] }],
  };
}

/**
 * Acrescenta um serviço à disciplina (RN-164: o par disciplina × serviço é único).
 *
 * A medida nasce do palpite pelo nome, como na importação, e marcada como palpite — é a
 * mesma regra: o classificador propõe, quem confere decide.
 */
export function addService(
  catalog: ServiceCatalog,
  input: { readonly disciplineKey: string; readonly name: string; readonly unit?: string },
): ServiceCatalog {
  const nome = input.name.trim();
  if (nome === '') return catalog;

  const disciplina = catalog.disciplines.find((item) => item.key === input.disciplineKey);
  if (disciplina === undefined) return catalog;

  const chave = serviceKey(disciplina.name, nome);
  if (disciplina.services.some((servico) => servico.key === chave)) return catalog;

  const unidade = input.unit?.trim();
  const measureBy = guessMeasureBy(nome, unidade);
  const appliesTo = guessAppliesTo(measureBy, nome, disciplina.name);
  const novo: CatalogService = {
    key: chave,
    name: nome,
    measureBy,
    ...(appliesTo === undefined ? {} : { appliesTo }),
    measureOrigin: 'PALPITE',
    items: [],
    quantityBySheet: {},
    ...(unidade === undefined || unidade === '' ? {} : { unit: unidade }),
  };

  return {
    ...catalog,
    disciplines: catalog.disciplines.map((item) =>
      item.key === disciplina.key ? { ...item, services: [...item.services, novo] } : item,
    ),
  };
}

export interface BuildCatalogInput {
  readonly fileName: string;
  readonly now: string;
  readonly sheets: readonly ClassifiedSheet[];
  /** Quais abas entram, pelo nome. Ausente = as que não são soma nem vazias. */
  readonly include?: ReadonlySet<string>;
  /**
   * O catálogo anterior, quando existe.
   *
   * Reimportar a planilha corrigida não pode apagar a medida que alguém escolheu serviço
   * a serviço — é o trabalho de uma tarde, e a planilha não o traz de volta. O que estava
   * **decidido** para um serviço que continua existindo continua valendo; o que era só
   * palpite é refeito, para que o classificador de hoje valha mais que o de ontem.
   */
  readonly previous?: ServiceCatalog;
}

interface ServicoEmMontagem {
  readonly key: string;
  readonly name: string;
  readonly unit?: string;
  readonly items: Map<string, CatalogItem>;
  /** Código de compras → a chave do item que já o carrega. Ver `resolveItemKey`. */
  readonly keyByCode: Map<string, string>;
  readonly quantityBySheet: Record<string, number>;
}

interface DisciplinaEmMontagem {
  readonly key: string;
  readonly name: string;
  readonly services: Map<string, ServicoEmMontagem>;
}

/**
 * Qual item do serviço é este — pelo código quando ele já apareceu, pela descrição quando
 * não.
 *
 * As duas metades importam, e nenhuma sozinha basta. **Pelo código só** quebra na aba que
 * esqueceu de codificar: o mesmo tijolo entra duas vezes, uma com `E-685110` e outra sem
 * código nenhum, e o catálogo passa a ter dois materiais onde a obra tem um. **Pela
 * descrição só** quebra do outro lado: "PRÉ MASSA" e "PRE-MASSA CINZA" com o mesmo código
 * são o mesmo saco de argamassa escrito por duas pessoas.
 *
 * Com as duas, o código manda quando existe e a descrição resolve o resto — que é o que
 * um almoxarife faria olhando a planilha.
 */
function resolveItemKey(servico: ServicoEmMontagem, item: ImportedItem): string {
  const conhecido = item.code === undefined ? undefined : servico.keyByCode.get(item.code);
  return conhecido ?? normalizeName(item.description);
}

/** O que já existia vence o que chega: a aba que codificou o material dita o código. */
function mergeItem(
  key: string,
  anterior: CatalogItem | undefined,
  item: ImportedItem,
  sheetName: string,
  onde: { readonly disciplineName: string; readonly serviceName: string },
  decidido: DecididoDoItem | undefined,
): CatalogItem {
  // O código que a planilha traz vale; sem ele, fica o que o catálogo já tinha — inclusive o
  // informado na tela (Fase 29, RN-395). Sem esta reserva, reimportar a pasta que não
  // codifica o material apagava o código que alguém escolheu no compras.
  const code = anterior?.code ?? item.code ?? decidido?.code;
  const unit = anterior?.unit ?? item.unit;
  const payment = anterior?.payment ?? item.payment;

  // A quantidade **soma** dentro da mesma aba: um serviço que lista o mesmo material em
  // duas linhas está pedindo o material duas vezes, e o almoxarife compra a soma.
  const quantityBySheet = { ...anterior?.quantityBySheet };
  if (item.quantity !== undefined) {
    quantityBySheet[sheetName] = (quantityBySheet[sheetName] ?? 0) + item.quantity;
  }

  // A classe escolhida por gente atravessa a reimportação; o palpite se refaz, para que o
  // classificador de hoje valha mais que o de ontem. Mesma regra da medida do serviço.
  const daPessoa = decidido?.kindOrigin === 'DECIDIDO' ? decidido : undefined;
  const kind =
    daPessoa?.kind ??
    anterior?.kind ??
    guessItemKind({
      disciplineName: onde.disciplineName,
      serviceName: onde.serviceName,
      description: item.description,
      ...(item.unit === undefined ? {} : { unit: item.unit }),
    });
  // A função é sempre escolha de gente — a planilha não traz função nenhuma, só o nome
  // dela em texto. Atravessa a reimportação enquanto o item continuar sendo mão de obra.
  const jobRoleId =
    kind === 'MAO_DE_OBRA' ? (anterior?.jobRoleId ?? decidido?.jobRoleId) : undefined;
  // O índice e a perda nunca vieram da planilha — foram digitados (Fase 24, D7). A
  // reimportação não tem o que dizer sobre eles, e por isso eles atravessam sempre, como a
  // espessura do serviço. A perda não atravessa para a mão de obra: gente não se perde (D8).
  const consumptionIndex = anterior?.consumptionIndex ?? decidido?.consumptionIndex;
  // A origem e a foto andam com o índice (Fase 40, RN-520): vêm de quem deu o índice.
  const deQuem = anterior?.consumptionIndex === undefined ? decidido : anterior;
  const lossPercent =
    kind === 'MAO_DE_OBRA' ? undefined : (anterior?.lossPercent ?? decidido?.lossPercent);
  // A bitola (Fase 27, RN-366): só no CORTE E DOBRA DE FERRAGEM, e só quando o texto do item
  // a escreve com a classe ("CA 50 - 16 MM"). Nos outros serviços nada muda — a planilha
  // antiga que punha o aço na MONTAGEM continua entrando como entrava, sem bitola, e a
  // gravação não a recusa por isso. Mão de obra não tem bitola.
  const steelGauge =
    kind === 'MAO_DE_OBRA' || !isSteelPurchaseService(onde.serviceName)
      ? undefined
      : (anterior?.steelGauge ?? decidido?.steelGauge ?? parseSteelGauge(item.description));

  return {
    key,
    description: anterior?.description ?? item.description,
    ...(code === undefined ? {} : { code }),
    ...(unit === undefined ? {} : { unit }),
    ...(payment === undefined ? {} : { payment }),
    quantityBySheet,
    kind,
    kindOrigin: daPessoa === undefined ? 'PALPITE' : 'DECIDIDO',
    ...(jobRoleId === undefined ? {} : { jobRoleId }),
    ...(consumptionIndex === undefined ? {} : { consumptionIndex }),
    ...(consumptionIndex === undefined || deQuem?.indexOrigin === undefined
      ? {}
      : { indexOrigin: deQuem.indexOrigin }),
    ...(consumptionIndex === undefined || deQuem?.indexAverage === undefined
      ? {}
      : { indexAverage: deQuem.indexAverage }),
    ...(lossPercent === undefined ? {} : { lossPercent }),
    ...(steelGauge === undefined ? {} : { steelGauge }),
  };
}

/** O que o catálogo anterior decidiu sobre um item — e que a reimportação não refaz. */
type DecididoDoItem = Pick<
  CatalogItem,
  | 'kind'
  | 'kindOrigin'
  | 'jobRoleId'
  | 'consumptionIndex'
  | 'indexOrigin'
  | 'indexAverage'
  | 'lossPercent'
  | 'steelGauge'
  | 'code'
>;

/**
 * A pasta virada em catálogo.
 *
 * O serviço aparece **uma vez**, com a união dos itens de todas as abas: a "Apartamento
 * Tipo 1" e a "Tipologia 4" listam o mesmo assentamento, e o que muda entre elas é a
 * quantidade, não o serviço. A união dos itens é de propósito — a aba que esqueceu de
 * codificar um material não apaga o código que a outra trouxe.
 */
export function buildCatalog(input: BuildCatalogInput): ServiceCatalog {
  const entram = input.sheets.filter((aba) =>
    input.include === undefined
      ? aba.scope === 'PARCELA' || aba.scope === 'PROPRIO'
      : input.include.has(aba.sheet.name) && aba.scope !== 'VAZIA',
  );

  /**
   * O que o catálogo anterior tinha para dizer sobre cada serviço.
   *
   * A **espessura** atravessa sempre: ela nunca veio da planilha — é digitada para o
   * serviço virar m³ —, e um arquivo novo não tem o que dizer sobre ela. A **margem** da
   * soleira e do peitoril, pelo mesmo motivo (RN-308). A **medida** atravessa só quando foi
   * decidida; ver `MeasureOrigin`.
   */
  const anteriores = new Map<
    string,
    Pick<
      CatalogService,
      'measureBy' | 'appliesTo' | 'measureOrigin' | 'layerThickness' | 'sideMargin'
    >
  >();
  /**
   * A classe decidida de cada item, a função dele, e o índice e a perda digitados (Fase 24),
   * por `serviceKey » itemKey`.
   */
  const classeDecidida = new Map<string, DecididoDoItem>();
  for (const disciplina of input.previous?.disciplines ?? []) {
    for (const servico of disciplina.services) {
      anteriores.set(servico.key, {
        measureBy: servico.measureBy,
        ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
        measureOrigin: servico.measureOrigin,
        ...(servico.layerThickness === undefined ? {} : { layerThickness: servico.layerThickness }),
        ...(servico.sideMargin === undefined ? {} : { sideMargin: servico.sideMargin }),
      });
      for (const item of servico.items) {
        classeDecidida.set(`${servico.key}»${item.key}`, {
          kind: item.kind,
          kindOrigin: item.kindOrigin,
          ...(item.jobRoleId === undefined ? {} : { jobRoleId: item.jobRoleId }),
          ...(item.consumptionIndex === undefined
            ? {}
            : { consumptionIndex: item.consumptionIndex }),
          ...(item.indexOrigin === undefined ? {} : { indexOrigin: item.indexOrigin }),
          ...(item.indexAverage === undefined ? {} : { indexAverage: item.indexAverage }),
          ...(item.lossPercent === undefined ? {} : { lossPercent: item.lossPercent }),
          ...(item.steelGauge === undefined ? {} : { steelGauge: item.steelGauge }),
          ...(item.code === undefined ? {} : { code: item.code }),
        });
      }
    }
  }

  /**
   * As taxas de aço de cada disciplina (Fase 27, RN-367). Como o índice, nunca vieram da
   * planilha numerada — foram digitadas, ou vieram do modelo de taxas —, e a reimportação não
   * tem o que dizer sobre elas: atravessam sempre.
   */
  const taxasAnteriores = new Map<string, readonly SteelRate[]>();
  const taxasPorElemento = new Map<string, readonly ElementSteelRates[]>();
  for (const disciplina of input.previous?.disciplines ?? []) {
    if (disciplina.steelRates !== undefined) {
      taxasAnteriores.set(disciplina.key, disciplina.steelRates);
    }
    if (disciplina.elementSteelRates !== undefined) {
      taxasPorElemento.set(disciplina.key, disciplina.elementSteelRates);
    }
  }

  const disciplinas = new Map<string, DisciplinaEmMontagem>();

  for (const aba of entram) {
    for (const disciplina of aba.sheet.disciplines) {
      const chaveDisciplina = normalizeName(disciplina.name);
      let alvo = disciplinas.get(chaveDisciplina);
      if (alvo === undefined) {
        alvo = { key: chaveDisciplina, name: disciplina.name, services: new Map() };
        disciplinas.set(chaveDisciplina, alvo);
      }

      for (const servico of disciplina.services) {
        const chave = serviceKey(disciplina.name, servico.name);
        let emMontagem = alvo.services.get(chave);
        if (emMontagem === undefined) {
          emMontagem = {
            key: chave,
            name: servico.name,
            ...(servico.unit === undefined ? {} : { unit: servico.unit }),
            items: new Map(),
            keyByCode: new Map(),
            quantityBySheet: {},
          };
          alvo.services.set(chave, emMontagem);
        }

        if (servico.quantity !== undefined) {
          emMontagem.quantityBySheet[aba.sheet.name] =
            (emMontagem.quantityBySheet[aba.sheet.name] ?? 0) + servico.quantity;
        }

        for (const item of servico.items) {
          const chaveItem = resolveItemKey(emMontagem, item);
          const juntado = mergeItem(
            chaveItem,
            emMontagem.items.get(chaveItem),
            item,
            aba.sheet.name,
            { disciplineName: disciplina.name, serviceName: servico.name },
            classeDecidida.get(`${chave}»${chaveItem}`),
          );
          emMontagem.items.set(chaveItem, juntado);
          if (juntado.code !== undefined) emMontagem.keyByCode.set(juntado.code, chaveItem);
        }
      }
    }
  }

  const incluidas = new Set(entram.map((aba) => aba.sheet.name));

  return {
    fileName: input.fileName,
    importedAt: input.now,
    sources: input.sheets.map((aba) => ({
      name: aba.sheet.name,
      scope: aba.scope,
      summary: aba.summary,
      imported: incluidas.has(aba.sheet.name),
    })),
    disciplines: [...disciplinas.values()].map((disciplina): CatalogDiscipline => ({
      key: disciplina.key,
      name: disciplina.name,
      ...(taxasAnteriores.get(disciplina.key) === undefined
        ? {}
        : { steelRates: taxasAnteriores.get(disciplina.key)! }),
      ...(taxasPorElemento.get(disciplina.key) === undefined
        ? {}
        : { elementSteelRates: taxasPorElemento.get(disciplina.key)! }),
      services: [...disciplina.services.values()].map((servico): CatalogService => {
        const anterior = anteriores.get(servico.key);
        const daPessoa = anterior?.measureOrigin === 'DECIDIDO' ? anterior : undefined;
        const measureBy = daPessoa?.measureBy ?? guessMeasureBy(servico.name, servico.unit);
        // O elemento segue a medida: decidido atravessa a reimportação junto com ela, e o
        // palpite se refaz pelo nome (RN-258).
        const appliesTo =
          daPessoa === undefined
            ? guessAppliesTo(measureBy, servico.name, disciplina.name)
            : daPessoa.appliesTo;
        return {
          key: servico.key,
          name: servico.name,
          ...(servico.unit === undefined ? {} : { unit: servico.unit }),
          measureBy,
          ...(appliesTo === undefined ? {} : { appliesTo }),
          measureOrigin: daPessoa === undefined ? 'PALPITE' : 'DECIDIDO',
          ...(anterior?.layerThickness === undefined
            ? {}
            : { layerThickness: anterior.layerThickness }),
          ...(anterior?.sideMargin === undefined ? {} : { sideMargin: anterior.sideMargin }),
          items: [...servico.items.values()],
          quantityBySheet: servico.quantityBySheet,
        };
      }),
    })),
  };
}

// ── O que a troca do catálogo leva embora ───────────────────────────────────────

/** Um serviço do catálogo anterior que a troca esvazia ou tira, com os itens que perde. */
export interface ServicoQueSai {
  readonly disciplineName: string;
  readonly serviceName: string;
  /** Quantos itens deste serviço deixam de existir. */
  readonly items: number;
  /** O serviço inteiro some do catálogo (e não só parte dos itens dele). */
  readonly removido: boolean;
}

export interface RemovidosDaTroca {
  /** Os serviços que perdem item ou que somem, na ordem do catálogo anterior. */
  readonly servicos: readonly ServicoQueSai[];
  /** Todos os itens que deixam de existir. */
  readonly items: number;
  /** Os serviços que somem por inteiro. */
  readonly servicosRemovidos: number;
}

/**
 * O que trocar o catálogo por outro tira da obra.
 *
 * `IMPORT_CATALOG` troca o catálogo, e o servidor grava o retrato inteiro (RN-255): o que o
 * catálogo novo não tem, a obra deixa de ter. A planilha numerada monta o catálogo só com as
 * abas, então os itens que a planilha plana tinha criado — e os que alguém acrescentou na tela
 * — saem junto. Esta conta existe para a tela dizer **quantos** antes de gravar.
 *
 * O item casa pela chave, pelo código de compras ou pela descrição: a planilha plana chaveia
 * pela descrição, e a numerada pode chavear pelo código, e o mesmo item não conta como
 * perdido por isso.
 */
export function removidosPelaTroca(
  anterior: ServiceCatalog | undefined,
  novo: ServiceCatalog,
): RemovidosDaTroca {
  const servicosNovos = new Map<string, CatalogService>();
  for (const disciplina of novo.disciplines) {
    for (const servico of disciplina.services) {
      servicosNovos.set(servico.key, servico);
      servicosNovos.set(
        `${normalizeName(disciplina.name)}|${normalizeName(servico.name)}`,
        servico,
      );
    }
  }

  const servicos: ServicoQueSai[] = [];
  for (const disciplina of anterior?.disciplines ?? []) {
    for (const servico of disciplina.services) {
      const correspondente =
        servicosNovos.get(servico.key) ??
        servicosNovos.get(`${normalizeName(disciplina.name)}|${normalizeName(servico.name)}`);

      const perdidos =
        correspondente === undefined
          ? servico.items.length
          : servico.items.filter(
              (item) =>
                !correspondente.items.some(
                  (outro) =>
                    outro.key === item.key ||
                    (item.code !== undefined &&
                      outro.code !== undefined &&
                      normalizeName(outro.code) === normalizeName(item.code)) ||
                    normalizeName(outro.description) === normalizeName(item.description),
                ),
            ).length;

      if (correspondente === undefined || perdidos > 0) {
        servicos.push({
          disciplineName: disciplina.name,
          serviceName: servico.name,
          items: perdidos,
          removido: correspondente === undefined,
        });
      }
    }
  }

  return {
    servicos,
    items: servicos.reduce((soma, servico) => soma + servico.items, 0),
    servicosRemovidos: servicos.filter((servico) => servico.removido).length,
  };
}

// ── Leitura do catálogo ─────────────────────────────────────────────────────────

export function catalogServices(catalog: ServiceCatalog): readonly CatalogService[] {
  return catalog.disciplines.flatMap((disciplina) => disciplina.services);
}

export function summarizeCatalog(catalog: ServiceCatalog): SheetSummary {
  const services = catalogServices(catalog);
  const items = services.flatMap((servico) => servico.items);
  return {
    disciplines: catalog.disciplines.length,
    services: services.length,
    items: items.length,
    codes: new Set(items.map((item) => item.code).filter((code) => code !== undefined)).size,
  };
}

/**
 * Alguma planilha foi importada?
 *
 * É a pergunta que separa os dois modos da tela. A obra montada **dentro do sistema** não
 * tem aba, não tem "o que a construtora orçou" e não tem diferença a mostrar — e falar
 * dessas três coisas ali seria a tela perguntando por algo que não existe. Com planilha,
 * a comparação volta a ser o assunto principal.
 */
export function hasImportedSheets(catalog: ServiceCatalog): boolean {
  return catalog.sources.some((source) => source.imported);
}

/** As abas que viraram quantitativo — as que uma tipologia pode apontar como referência. */
export function importedSheetNames(catalog: ServiceCatalog): readonly string[] {
  return catalog.sources.filter((source) => source.imported).map((source) => source.name);
}
